import { Injectable } from '@angular/core';
import { ActiveCartService } from '@cart/data-access';
import { CheckoutActions as CheckoutActionsV2 } from '@checkout-v2/data-access';
import { CheckoutActions } from '@checkout/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OccInitCardBindingResponse, Payment } from '@payment/const';
import { OccPaymentService, PaymentActions } from '@payment/data-access';
import { WindowRef } from '@spartacus/core';
import { Observable, combineLatest, iif, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class PaymentEffects {
  loadPaymentList$: Observable<PaymentActions.LoadPaymentListSuccess | PaymentActions.LoadPaymentListFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<PaymentActions.LoadPaymentList>(PaymentActions.LOAD_PAYMENT_LIST),
        switchMap(() =>
          this.occPaymentService.getPayments().pipe(
            map((payments: Payment[]) => new PaymentActions.LoadPaymentListSuccess(payments)),
            catchError((e) => of(new PaymentActions.LoadPaymentListFail(e))),
          ),
        ),
      ),
    );

  deletePayment$: Observable<PaymentActions.DeletePaymentSuccess | PaymentActions.DeletePaymentFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PaymentActions.DeletePayment>(PaymentActions.DELETE_PAYMENT),
        switchMap(({ payload: bindingId }) =>
          this.occPaymentService.deletePayment(bindingId).pipe(
            map(() => new PaymentActions.DeletePaymentSuccess()),
            catchError((e) => of(new PaymentActions.DeletePaymentFail(e))),
          ),
        ),
      ),
  );

  bindPayment$: Observable<PaymentActions.BindPaymentSuccess | PaymentActions.BindPaymentFail> = createEffect(() =>
    this.actions$.pipe(
      ofType<PaymentActions.BindPayment>(PaymentActions.BIND_PAYMENT),
      withLatestFrom(combineLatest([this.getCartId()])),
      switchMap(([{ payload: bindingId }, [cartId]]) =>
        iif(
          () => bindingId !== null,
          this.occPaymentService.bindPayment(cartId, bindingId),
          this.occPaymentService.unbindPayment(cartId),
        ).pipe(
          map(() => new PaymentActions.BindPaymentSuccess(bindingId)),
          catchError((e) => of(new PaymentActions.BindPaymentFail(e))),
        ),
      ),
    ),
  );

  reloadPaymentList$: Observable<PaymentActions.LoadPaymentList> = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentActions.DELETE_PAYMENT_FAIL, PaymentActions.DELETE_PAYMENT_SUCCESS),
      map(() => new PaymentActions.LoadPaymentList()),
    ),
  );

  initCardBindingOnExternalAddress$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.INIT_CARD_BINDING, CheckoutActionsV2.INIT_CARD_BINDING),
        filter(Boolean),
        switchMap(() => {
          if (!this.windowRef.nativeWindow) {
            return;
          }

          const returnUrl = this.windowRef.nativeWindow.location.origin + this.windowRef.nativeWindow.location.pathname;

          return this.occPaymentService.initCardBindingOnExternalAddress(returnUrl).pipe(
            map(({ paymentUrl }: OccInitCardBindingResponse) => {
              if (!this.windowRef.nativeWindow) {
                return;
              }
              this.windowRef.nativeWindow.location.href = paymentUrl;
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private occPaymentService: OccPaymentService,
    private activeCartService: ActiveCartService,
    private windowRef: WindowRef,
  ) {}

  private getCartId(): Observable<string> {
    return this.activeCartService.getActiveCartId();
  }
}
